import {CLSelectorOptionValue} from 'common/src/components/Selectors/CLSelector/CLSelector.types';

export enum CLPopperFlip {
  left = 'left',
  right = 'right',
  top = 'top',
  bottom = 'bottom',
}

export interface CLRemoteEnums {
  optionsPerEnum: Record<string, CLRemoteEnum[]>;
}

export interface CLRemoteEnum {
  name: string;
  value: string;
  uiName: string;
  title?: string;
  learnMoreUrl?: string;
  description?: string;
  tags?: Array<string>;
  category?: string;
  campaignTypes?: number[];
}

export interface CLEnum<Name extends string = string, Value extends number = number>
  extends Omit<CLRemoteEnum, 'value'> {
  value: Value;
  name: Name;
}

export enum CLEnumType {
  General,
  CampaignPage,
  Social,
  RelatedItems,
  Users,
  ExcelReports,
  Templates,
  Feeds,
  AssetsLibrary,
  Partners,
  AutomaticRules,
  ActionsTracking,
  DataSources,
  ClientPage,
  Fonts,
  CreativeScore,
}

export interface EnumForSelector<TValue extends CLSelectorOptionValue = string> {
  value: TValue;
  name: string;
  uiName: string;
  label: string;
  title?: string;
  learnMoreUrl?: string;
  description?: string;
  tags?: string[];
  category?: string;
  campaignTypes?: number[];
  isDeprecated?: boolean;
  isDisabled?: boolean;
}

export interface CLEnumsMapper {
  readonly byValue: Record<string, CLEnum>;
  readonly byName: Record<string, CLEnum>;
  readonly enumsForSelector: EnumForSelector[];
}

export const CLEnumKeys = {
  CampaignPage: {
    CampaignType: 'Campaigns.CampaignType',
    CampaignStatus: 'Campaigns.ObjectStatus',
    CampaignContentType: 'Campaigns.CampaignContentType',
    LocationTargetingMethod: 'Campaigns.LocationTargetingMethodEnum',
    LocationFallbackOption: 'Campaigns.LocationFallbackOptionEnum',
    PredicateConditionOperator: 'ObjectModel.PredicateConditionOperator',
    OptimizeByEnum: 'Campaigns.OptimizeByEnum',
    CreativeOptimizationGroupEnum: 'Campaigns.CreativeOptimizationGroupEnum',
    AeScriptItemPropertyTypeEnum: 'ObjectModel.AeScriptItemPropertyTypeEnum',
    WeatherProviders: 'Campaigns.WeatherProviderEnum',
    LandingPageSource: 'Templates.LandingPageSource',
    ObjectStatus: 'Campaigns.ObjectStatus',
    TimezoneSourceOptionEnum: 'ObjectModel.TimezoneSourceOptionEnum',
    MediaStatus: 'Medias.MediaStatusEnum',
    MediaType: 'Medias.MediaTypeEnum',
    ShortMediaType: 'Related.ShortMediaType',
    EntitySelection: 'Campaigns.EntitySelectionOptionEnum',
    EnvironmentEnum: 'Tracking.EnvironmentEnum',
    AnimationLengthEnum: 'Campaigns.AnimationLengthEnum',
    CampaignVersion: 'Campaigns.CampaignVersion',
    PlacementRotationTypeEnum: 'Campaigns.PlacementRotationTypeEnum',
    PlacementEventTypes: 'Campaigns.CampaignPlacementEventTypeEnum',
    PlacementCustomParams: 'Campaigns.PlacementCustomParamsKeyEnum',
    DspParameter: 'DSPs.DspParameter',
    AdPlatform: 'Videos.AdPlatform',
    DoubleVerifyMedia: 'DoubleVerify.MediaFilter',
    CopyAdSchedulingOptions: 'AdScheduling.CopyAdSchedulingOptions',
    ProductSelectionOperator: 'ProductSelection.Operator',
    ProductSelectionOrder: 'ProductSelection.Order',
    AnimationLength: 'Campaigns.AnimationLengthEnum',
    ContentSelectionStrategy: 'Campaigns.ContentSelectionStrategy',
  },
  General: {
    ProductType: 'Websites.ProductTypeEnum',
    Verticals: 'Products.ClientVerticalEnum',
    TemplateType: 'ObjectModel.TemplateType',
    CreativeType: 'ObjectModel.CreativeType',
    ExternalConnectionStatus: 'ExternalAssets.ConnectionStatus',
    NotificationCategory: 'Shared.ObjectModel.NotificationsCenter.NotificationCategory',
    NotificationSeverity: 'Shared.ObjectModel.NotificationsCenter.NotificationSeverity',
    NotificationState: 'Shared.ObjectModel.NotificationsCenter.NotificationState',
    VastVersion: 'VastVersion',
    AdServingEventTypeEnum: 'Kinesis.AdServingEventTypeEnum',
    WebsiteEventTypeEnum: 'Websites.WebsiteEventTypeEnum',
    ClientType: 'ClientType',
    AgencyLinkInitiatedBy: 'AgencyLinkInitiateBy',
    AgencyLinkStatus: 'AgencyLinkStatus',
    AgencyPermissionScope: 'AgencyPermissionScope',
  },
  Social: {
    AssetType: 'Integrations.AssetType',
    Platform: 'Integrations.ExternalPlatform',
    SocialFieldsPreset: 'Social.FieldsPreset',
    ExternalAdsStatus: 'Social.ExternalAdsStatusEnum',
  },
  RelatedItems: {
    ConditionOperators: 'ObjectModel.PredicateConditionOperator',
    RelationType: 'Feeds.RelationType',
    FeedColumns: 'Feeds.FeedColumn',
    RetrievalType: 'Feeds.RetrievalType',
    OrderByOptions: 'Feeds.OrderByOptions',
    TopItemsSelectOptionEnum: 'Feeds.TopItemsSelectOptionEnum',
  },
  Users: {
    ClinchApiRoles: 'users.ClinchApiRoles',
  },
  ExcelReport: {
    JobExecution: 'GeneralJobs.ScheduledJobExecutionStatus',
  },
  Templates: {
    DynamicParameters: 'Templates.TemplateDynamicParameterCategory',
    DynamicParameterType: 'Templates.TemplateDynamicParameterType',
  },
  Feeds: {
    FeedType: 'Feeds.FeedTypeEnum',
    FeedUploadMethod: 'Feeds.FeedUploadMethodTypeEnum',
    ItemAvailabiltyConfig: 'Configuration.ItemAvailabiltyConfig',
    DefaultImages: 'DefaultImages.DefaultImageTypeEnum',
    Capitalization: 'Feeds.Capitalization',
    SportsProvider: 'APIs.SportsProviderEnum',
    SportsTypeEnum: 'APIs.SportsTypeEnum',
  },
  AssetsLibrary: {
    AssetsFolders: 'AssetsLibrary.AssetsFoldersEnum',
  },
  AutomaticRules: {
    AutomaticRuleActionEnum: 'AutomaticRules.AutomaticRuleActionEnum',
    AutomaticRuleSignalTypeEnum: 'AutomaticRules.AutomaticRuleSignalTypeEnum',
    ProfileTagGroupTypeEnum: 'AutomaticRules.ProfileTagGroupTypeEnum',
    PredicateConditionOperator: 'ObjectModel.PredicateConditionOperator',
  },
  Partners: {
    AdServingPartnerType: 'AdservingPartnerType',
  },
  ActionTracking: {
    ActionType: 'ActionTracking.ActionType',
    ObjectType: 'ActionTracking.ObjectType',
  },
  DataSources: {
    DataSourceType: 'DataSources.DataSourceTypeEnum',
    ExcelPixelDocumentType: 'ExcelReports.ExcelPixelDocumentType',
  },
  ClientPage: {
    AnalyticsCustomDimensionOptionEnum: 'Analytics.AnalyticsCustomDimensionOptionEnum',
    IdentityProvidersEnum: 'clients.IdentityProvidersEnum',
    WhitelistedCapabilities: 'clients.WhitelistedCapabilities',
    AgreementTypeEnum: 'clients.AgreementTypeEnum',
    ClinchTeamEnum: 'clients.ClinchTeamEnum',
    CustomerRegionEnum: 'clients.CustomerRegionEnum',
    HoldingGroupEnum: 'clients.HoldingGroupEnum',
    MediaAgencyEnum: 'clients.MediaAgencyEnum',
    MsaTypeEnum: 'clients.MsaTypeEnum',
    ServiceTypeEnum: 'clients.ServiceTypeEnum',
  },
  Fonts: {
    SupportedSubsetLanguage: 'Fonts.SupportedSubsetLanguage',
  },
  CreativeScore: {
    Gender: 'CreativeScores.Gender',
    DeviceType: 'CreativeScores.DeviceType',
    Age: 'CreativeScores.Age',
    Platform: 'CreativeScores.Platform',
  },
};
